import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { getWebsiteEnquiries, updateEnquiryStatus } from '../../services/websiteEnquiryApi';

const WebsiteEnquiryManager = ({ token }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [organizationTypeFilter, setOrganizationTypeFilter] = useState('');
  const [interestAreaFilter, setInterestAreaFilter] = useState('');
  const [updating, setUpdating] = useState(false);
  const [note, setNote] = useState('');

  // Status options
  const statusOptions = [
    { value: 'new', label: 'New', color: 'primary' },
    { value: 'contacted', label: 'Contacted', color: 'info' },
    { value: 'qualified', label: 'Qualified', color: 'success' },
    { value: 'converted', label: 'Converted', color: 'success' },
    { value: 'archived', label: 'Archived', color: 'default' }
  ];

  // Organization type mapping
  const organizationTypes = {
    'nhs-trust': 'NHS Trust',
    'private-clinic': 'Private Clinic',
    'university': 'University',
    'charity': 'Charity',
    'corporate': 'Corporate',
    'other': 'Other'
  };

  // Interest area mapping
  const interestAreas = {
    'pilot-program': 'Pilot Program',
    'research-collaboration': 'Research Collaboration',
    'implementation': 'Full Implementation',
    'general-enquiry': 'General Enquiry'
  };

  useEffect(() => {
    if (token) {
      fetchEnquiries();
    }
  }, [token, page, rowsPerPage, statusFilter, organizationTypeFilter, interestAreaFilter]);

  const fetchEnquiries = async () => {
    setLoading(true);
    setError(null);
    
    console.log('WebsiteEnquiryManager - Token:', token ? 'Present' : 'Missing');
    
    try {
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage
      };
      
      if (statusFilter) params.status = statusFilter;
      if (organizationTypeFilter) params.organizationType = organizationTypeFilter;
      if (interestAreaFilter) params.interestArea = interestAreaFilter;
      
      const response = await getWebsiteEnquiries(params, token);
      setEnquiries(response.inquiries || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      setError('Failed to fetch enquiries');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setDialogOpen(true);
    setNote('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEnquiry(null);
    setNote('');
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedEnquiry) return;
    
    setUpdating(true);
    try {
      await updateEnquiryStatus(selectedEnquiry._id, {
        status: newStatus,
        note: note || undefined
      }, token);
      
      // Update local state
      setEnquiries(enquiries.map(enq => 
        enq._id === selectedEnquiry._id 
          ? { ...enq, status: newStatus }
          : enq
      ));
      
      handleCloseDialog();
      fetchEnquiries(); // Refresh to get updated data
    } catch (err) {
      setError('Failed to update enquiry status');
      console.error('Error:', err);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = statusOptions.find(opt => opt.value === status) || statusOptions[0];
    return (
      <Chip
        label={statusConfig.label}
        color={statusConfig.color}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && enquiries.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Website Enquiries
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Organization Type</InputLabel>
              <Select
                value={organizationTypeFilter}
                onChange={(e) => setOrganizationTypeFilter(e.target.value)}
                label="Organization Type"
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(organizationTypes).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Interest Area</InputLabel>
              <Select
                value={interestAreaFilter}
                onChange={(e) => setInterestAreaFilter(e.target.value)}
                label="Interest Area"
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(interestAreas).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => {
                setStatusFilter('');
                setOrganizationTypeFilter('');
                setInterestAreaFilter('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Enquiries Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Interest</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enquiries.map((enquiry) => (
              <TableRow key={enquiry._id} hover>
                <TableCell>{formatDate(enquiry.createdAt)}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {enquiry.organization}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{enquiry.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {enquiry.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={organizationTypes[enquiry.organizationType] || enquiry.organizationType}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={interestAreas[enquiry.interestArea] || enquiry.interestArea}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{getStatusChip(enquiry.status)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleViewEnquiry(enquiry)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {enquiries.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No enquiries found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* View/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedEnquiry && (
          <>
            <DialogTitle>
              Enquiry Details
              <Box sx={{ float: 'right' }}>
                {getStatusChip(selectedEnquiry.status)}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Contact Information
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                          Name:
                        </Typography>
                        <Typography variant="body1">
                          {selectedEnquiry.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                          Title:
                        </Typography>
                        <Typography variant="body1">
                          {selectedEnquiry.title || 'Not provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                        <Typography variant="body1">
                          <a href={`mailto:${selectedEnquiry.email}`}>
                            {selectedEnquiry.email}
                          </a>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                        <Typography variant="body1">
                          {selectedEnquiry.phone || 'Not provided'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Organization Details
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BusinessIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                        <Typography variant="body1">
                          {selectedEnquiry.organization}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CategoryIcon sx={{ mr: 1, color: 'action.active' }} fontSize="small" />
                        <Chip
                          label={organizationTypes[selectedEnquiry.organizationType]}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Interest Area:
                        </Typography>
                        <Chip
                          label={interestAreas[selectedEnquiry.interestArea]}
                          color="secondary"
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                {selectedEnquiry.message && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Message
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedEnquiry.message}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Update Status
                      </Typography>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>New Status</InputLabel>
                        <Select
                          value={selectedEnquiry.status}
                          onChange={(e) => setSelectedEnquiry({
                            ...selectedEnquiry,
                            status: e.target.value
                          })}
                          label="New Status"
                        >
                          {statusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Add Note (Optional)"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add any internal notes about this enquiry..."
                      />
                    </CardContent>
                  </Card>
                </Grid>
                {selectedEnquiry.notes && selectedEnquiry.notes.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Notes History
                        </Typography>
                        {selectedEnquiry.notes.map((note, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(note.addedAt)} - {note.addedBy}
                            </Typography>
                            <Typography variant="body2">
                              {note.text}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted: {formatDate(selectedEnquiry.createdAt)}
                    {selectedEnquiry.updatedAt && selectedEnquiry.updatedAt !== selectedEnquiry.createdAt && (
                      <> | Last Updated: {formatDate(selectedEnquiry.updatedAt)}</>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                onClick={() => handleUpdateStatus(selectedEnquiry.status)}
                variant="contained"
                disabled={updating}
              >
                {updating ? <CircularProgress size={20} /> : 'Update Status'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default WebsiteEnquiryManager;